import { MarkerClusterer } from "@googlemaps/markerclusterer";

import LogUtils from './LogUtils';
import ValidationUtils from './ValidationUtils';
import VisualizationUtils from './VisualizationUtils';
import settings from '../settings/settings';

export const mapStyle = {
  marker: {
    regular: {
      fill: "#333333"
    }
  },
  polygons: {
    dangerLevelGradient: {
      l0: {
        stroke: "#fefefe",
        fill: "#fefefe",
        hover: {
          stroke: "#fafafa",
          fill: "#fafafa"
        }
      },
      l1: {
        stroke: "#0D6FF2",
        fill: "#0D6FF2",
        hover: {
          stroke: "#1C6CE8",
          fill: "#1C6CE8"
        }
      },
      l2: {
        stroke: "#1C6CE8",
        fill: "#1C6CE8",
        hover: {
          stroke: "#3565D4",
          fill: "#3565D4"
        }
      },
      l3: {
        stroke: "#3565D4",
        fill: "#3565D4",
        hover: {
          stroke: "#525AB9",
          fill: "#525AB9"
        }
      },
      l4: {
        stroke: "#525AB9",
        fill: "#525AB9",
        hover: {
          stroke: "#4C5DC0",
          fill: "#4C5DC0"
        }
      },
      l5: {
        stroke: "#4C5DC0",
        fill: "#4C5DC0",
        hover: {
          stroke: "#6F509F",
          fill: "#6F509F"
        }
      },
      l6: {
        stroke: "#6F509F",
        fill: "#6F509F",
        hover: {
          stroke: "#864888",
          fill: "#864888"
        }
      },
      l7: {
        stroke: "#864888",
        fill: "#864888",
        hover: {
          stroke: "#9D3F70",
          fill: "#9D3F70"
        }
      },
      l8: {
        stroke: "#9D3F70",
        fill: "#9D3F70",
        hover: {
          stroke: "#B83657",
          fill: "#B83657"
        }
      },
      l9: {
        stroke: "#B83657",
        fill: "#B83657",
        hover: {
          stroke: "#D12B3B",
          fill: "#D12B3B"
        }
      },
      l10: {
        stroke: "#D12B3B",
        fill: "#D12B3B",
        hover: {
          stroke: "#F41C09",
          fill: "#F41C09"
        }
      },
      l11: {
        stroke: "#F41C09",
        fill: "#F41C09",
        hover: {
          stroke: "#e41301",
          fill: "#e41301"
        }
      }
    }
  },
  visualizer: {
    mapTitle: {
      color: "#242644",
    },
    actionsContainer: {
      background: "#ffffff"
    }
  },
  polygon: {
    stroke: "#4DBEAD",
    fill: "#45B1B5",
  },
  disabledPolygon: {
    stroke: "#F7FAFB",
    fill: "#F7FAFB",
  },
  polygonEditMode: {
    stroke: "#F75B01",
    fill: "#F75B01",
  }
};
const polygonsOpts = {
  STROKE_OPACITY: 0.6,
  STROKE_WEIGHT: 1,
  FILL_OPACITY: 0.4,
  STROKE_COLOR: mapStyle.polygons.stroke,
  FILL: mapStyle.polygons.fill,
  hover: {
    STROKE_OPACITY: 0.85,
    STROKE_WEIGHT: 1,
    FILL_OPACITY: 0.85,
    STROKE_COLOR: mapStyle.disabledPolygon.stroke,
    FILL: mapStyle.disabledPolygon.fill,
  }
};
const imageUtils = {
  PATH_CLUSTER_ICONS: `${settings.BASE_PATH_IMAGES_FOLDER}/ico-marker-clusterer`,
  ICO_MAP_MARKER_USER: `${settings.BASE_PATH_IMAGES_FOLDER}/ico-user-location`,
  ICO_MAP_MARKER_OPENED_FOCUS: `${settings.BASE_PATH_IMAGES_FOLDER}/ico-map-marker-denounce-opened`,
  ICO_MAP_MARKER_IN_PROGRESS_FOCUS: `${settings.BASE_PATH_IMAGES_FOLDER}/ico-map-marker-denounce-in-progress`,
  ICO_MAP_MARKER_CLOSED_FOCUS: `${settings.BASE_PATH_IMAGES_FOLDER}/ico-map-marker-denounce-closed`
}
export const markerIcons = {
  'user': {
    icon: `${imageUtils.ICO_MAP_MARKER_USER}.svg`
  },
  'opened': {
    icon: `${imageUtils.ICO_MAP_MARKER_OPENED_FOCUS}.svg`
  },
  'in-progress': {
    icon: `${imageUtils.ICO_MAP_MARKER_IN_PROGRESS_FOCUS}.svg`
  },
  'closed': {
    icon: `${imageUtils.ICO_MAP_MARKER_CLOSED_FOCUS}.svg`
  }
};

const TAG = "MapUtils";
const validationUtils = new ValidationUtils();
const visuUtils = new VisualizationUtils();
const logUtils = new LogUtils();

function createPolygon({ info, editable, bounds, colors }) {
  try {
    if (
      !validationUtils.isValidObj(bounds) ||
      !info ||
      !Array.isArray(bounds) ||
      bounds.length === 0
    ) {
      return false;
    }
    info.polygonColor = visuUtils.getVectorSeriousnessColors(
      info.seriousnessScoreNumOpenedVectors,
      mapStyle.polygons.dangerLevelGradient
    );
    const customColors = info.polygonColor || colors;
    const validBounds = bounds.map((bound) => {
      return {
        lat: Number(bound?.lat),
        lng: Number(bound?.lng)
      }
    });
    const newPolygon = new window.google.maps.Polygon({
      paths: validBounds,
      editable: validationUtils.isValidObj(editable) ? editable : false,
      strokeOpacity: polygonsOpts.STROKE_OPACITY,
      strokeWeight: polygonsOpts.STROKE_WEIGHT,
      fillOpacity: polygonsOpts.FILL_OPACITY,
      strokeColor: customColors ? customColors.stroke ? customColors.stroke : polygonsOpts.STROKE_COLOR : polygonsOpts.STROKE_COLOR,
      fillColor: customColors ? customColors.fill ? customColors.fill : polygonsOpts.FILL : polygonsOpts.FILL
    });
    newPolygon.info = info;
    return newPolygon;

  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to createPolygon", err);
  }
  return false;
}

function createPin({ map, pin, addEventListenerClickPin, handleClickMarkerInfo }) {
  try {
    if (!map || !pin?.lat || !pin?.lng || !pin?.info?.denounceStatus?.name) {
      return null;
    }
    const icon = markerIcons[pin.info.denounceStatus.name].icon;
    const validPosition = {
      lat: Number(pin.lat),
      lng: Number(pin.lng)
    };
    let marker = icon ?
      new window.google.maps.Marker({
        position: validPosition,
        map: map,
        title: pin.description ? pin.description : "",
        icon: icon
      }) :
      new window.google.maps.Marker({
        position: validPosition,
        map: map,
        title: pin.description ? pin.description : ""
      });

    marker.type = pin.info.denounceStatus.name;
    const infoWindowMsg = visuUtils.fetchPinMsgWithParams(pin.info);

    if (infoWindowMsg) {
      const infoWindow = new window.google.maps.InfoWindow({
        content: infoWindowMsg
      });
      addEventListenerClickPin(marker, infoWindow);
      infoWindow.addListener('click', () => {
        handleClickMarkerInfo(pin?.info?.id);
      });
    }
    return marker;

  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to createPin", err);
  }
  return null;
}

export function createUserLocationPin({ map, userPosition }) {
  try {
    if (!map || !userPosition) return null;

    const validPosition = {
      lat: Number(userPosition.lat),
      lng: Number(userPosition.lng)
    };
    let marker = new window.google.maps.Marker({
      position: validPosition,
      title: "Sua localização",
      icon: markerIcons.user.icon
    });

    const infoWindowMsg = visuUtils.fetchPinMsgWithParams({
      description: "Sua localização"
    });

    if (infoWindowMsg) {
      const infoWindow = new window.google.maps.InfoWindow({
        content: infoWindowMsg
      });
      addEventListenerClickPin(marker, infoWindow);
    }
    return marker;

  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to createUserLocationPin", err);
  }
  return null;
}

export function createHeatmap(map, heatmapData) {
  try {
    if (!map || !heatmapData) return false;

    const listLatLngs = heatmapData.map((item) => {
      if (!item?.geo_location?.lat || !item?.geo_location?.lng) return false;

      const { lat, lng } = item.geo_location;
      const seriousnessScore = item.seriousnessScore ? item.seriousnessScore : 0;

      return {
        location: new window.google.maps.LatLng(lat, lng),
        weight: seriousnessScore
      };
    });

    return new window.google.maps.visualization.HeatmapLayer({
      data: listLatLngs,
      radius: 30
    });
  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to createHeatmap", err);
  }
  return null;
}

function createInfoWindow({ map, latLng, info }) {
  try {
    if (!map || !latLng || !info) return false;

    let infoWindow = new window.google.maps.InfoWindow({
      size: new window.google.maps.Size(150, 50)
    });
    const description = info?.description?.replace(":_:", " de ");

    infoWindow.setContent(description);
    infoWindow.setPosition(latLng);
    infoWindow.open(map);

    return true;

  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to createInfoWindow", err);
  }
  return false;
}

function equalCoordinates(coordA, coordB) {
  if (!coordA?.lat || !coordA?.lng || !coordB?.lat || !coordB?.lng) return false;
  return (coordA.lat === coordB.lat) && (coordA.lng === coordB.lng);
}

function isCoordinateWithinPolygon(coordinates, polygon) {
  try {
    if (!polygon || !coordinates?.lat || !coordinates?.lng) return false;

    let latLng = new window.google.maps.LatLng(coordinates.lat, coordinates.lng);

    if (!latLng) return false;

    return window.google.maps.geometry.poly.containsLocation(latLng, polygon);

  } catch (err) {
    logUtils.logErrorMsg(TAG, "Failed on isCoordinateWithinPolygon", err);
  }
  return false;
}

export function isMapInstanceGoogleMaps(map) {
  if (!(map instanceof window.google.maps.Map)) return false;
  return true;
}

function getClusterRenderer(markerTypeImageUrl) {
  return {
    render: ({ count, position }, stats) => {
      return new window.google.maps.Marker({
        position,
        icon: {
          url: markerTypeImageUrl,
          scaledSize: new window.google.maps.Size(50, 50),
        },
        label: {
          text: String(count),
          color: "rgba(255,255,255,0.9)",
          fontSize: "13px",
        },
        title: `Cluster of ${count} markers`,
        zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
      });
    }
  }
}

function setSelectedPolygon(newPolygon, setSelectedShape) {
  try {
    let vertices = newPolygon.getPath();
    let verticiesObj = [];

    for (let i = 0; i < vertices.getLength(); i++) {
      let xy = vertices.getAt(i);
      let coord = {
        lat: xy.lat(),
        lng: xy.lng()
      }
      verticiesObj.push(coord);
    }

    setSelectedShape(verticiesObj);

  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to setSelectedPolygon", err);
  }
}

export function loadPolygons({ map, polygons, focusedGeoLocation, useGeojsonBounds, callbackAddPolygon,
  geoLocationToLoad, onPolygonClicked }) {
  try {
    if (!map || !polygons) return false;

    const loadedPolygons = polygons.map((polygon) => {
      if (!polygon || equalCoordinates(polygon.geo_location, geoLocationToLoad)) {
        return null;
      }

      const newPolygon = createPolygon({
        info: polygon,
        editable: false,
        bounds: useGeojsonBounds ? polygon?.geo_location?.geojson_bounds : polygon?.bounds
      });

      if (!newPolygon) return null;
      if (isCoordinateWithinPolygon(focusedGeoLocation, newPolygon)) {
        newPolygon.setOptions({
          strokeColor: mapStyle.polygons.stroke,
          fillColor: mapStyle.polygons.fill,
        })
      }

      newPolygon.setMap(map);

      addEventListenerClickPolygon(map, newPolygon, true, onPolygonClicked);
      addEventListenerHoverPolygon(newPolygon);

      if (callbackAddPolygon) callbackAddPolygon(newPolygon);
      return newPolygon;
    })
      .filter(item => item !== null);
    return loadedPolygons;
  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to loadPolygons", err);
  }
  return [];
}

export function loadSpecificPolygon(readOnly, isEditEnabled, map, geoLocationToLoad, geoLocationBeingEdited, setSelectedShape) {
  try {
    let selectedPolygon = [];

    if (readOnly || isEditEnabled) {
      selectedPolygon = geoLocationToLoad ? geoLocationToLoad.bounds ? geoLocationToLoad.bounds : null : null;
    } else {
      selectedPolygon = geoLocationBeingEdited;
    }

    if (!selectedPolygon) return false;

    let currentPolygon = createPolygon({
      info: geoLocationToLoad,
      editable: !readOnly && isEditEnabled,
      bounds: selectedPolygon,
      colors: {
        stroke: mapStyle.polygonsEditMode.stroke,
        fill: mapStyle.polygonsEditMode.fill,
      }
    });

    if (!currentPolygon) return false;

    currentPolygon.setMap(map);

    if (!readOnly && isEditEnabled) {
      addEventListenerEditPolygon(currentPolygon, setSelectedShape);
    }

  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to loadSpecificPolygon", err);
  }
}

export function loadPins({ map, pins, markers, markersClusterer, setMarkers, setMarkersClusterer,
  handleClickMarkerInfo }) {
  try {
    if (!map || !Array.isArray(pins)) return false;

    const markersByType = {}

    pins.forEach((pin) => {
      const marker = createPin({ map, pin, addEventListenerClickPin, handleClickMarkerInfo });

      if (marker) {
        if (!markersByType[marker.type]) {
          markersByType[marker.type] = {
            type: marker.type,
            markers: []
          };
        }

        const updatedMarkers = markers;
        updatedMarkers.push(marker);

        setMarkers(updatedMarkers);
        markersByType[marker.type].markers.push(marker);
      }
    });

    Object.values(markersByType).forEach((markersByType) => {
      if (markersByType.markers && markersByType.type) {
        const markerTypeImage = `${imageUtils.PATH_CLUSTER_ICONS}-${markersByType.type}/1.svg`;
        const newClusterer = new MarkerClusterer({
          map,
          markers: markersByType.markers,
          renderer: getClusterRenderer(markerTypeImage)
        });
        const updatedListClusterer = markersClusterer;
        updatedListClusterer.push(newClusterer);
        setMarkersClusterer(updatedListClusterer);
      }
    });
  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to loadPins", err);
  }
}

export function addEventListenersCreatePolygon(drawingManager, setSelectedShape, newShape) {
  try {
    window.google.maps.event.addListener(drawingManager, 'overlaycomplete', function (e) {
      newShape = e.overlay;
      newShape.type = e.type;

      if (e.type !== window.google.maps.drawing.OverlayType.MARKER) {
        // Switch back to non-drawing mode after drawing a shape.
        drawingManager.setDrawingMode(null);

        // Add an event listener that selects the newly-drawn shape when the user mouses down on it.
        window.google.maps.event.addListener(newShape, 'click', function (e) {

          if (e.vertex !== undefined) {

            if (newShape.type === window.google.maps.drawing.OverlayType.POLYGON) {
              let path = newShape.getPaths().getAt(e.path);
              path.removeAt(e.vertex);

              if (path.length < 3) {
                newShape.setMap(null);
              }
            }
          }

          setSelectedPolygon(newShape, setSelectedShape);
        });

        // This event fires when a new polygon is created manually
        window.google.maps.event.addListener(drawingManager, 'polygoncomplete', function (event) {
          setSelectedPolygon(newShape, setSelectedShape);
        });

      } else {
        window.google.maps.event.addListener(newShape, 'click', function (e) {
          setSelectedPolygon(newShape, setSelectedShape);
        });
      }
    });
  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to addEventListenersCreatePolygon", err);
  }
}

function addEventListenerEditPolygon(polygon, setSelectedShape) {
  try {
    if (!polygon) return false;

    window.google.maps.event.addListener(polygon.getPath(), 'insert_at', function (index, obj) {
      setSelectedPolygon(polygon, setSelectedShape);
    });
    window.google.maps.event.addListener(polygon.getPath(), 'set_at', function (index, obj) {
      setSelectedPolygon(polygon, setSelectedShape);
    });
  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to addEventListenerEditPolygon", err);
  }
}

function addEventListenerClickPolygon(map, polygon, withInfoWindow, onPolygonClicked) {
  try {
    if (!polygon) return false;

    window.google.maps.event.addListener(polygon, 'click', function (event) {
      onPolygonClicked?.(polygon.info);
      if (withInfoWindow) createInfoWindow({
        map: map,
        latLng: event.latLng,
        info: polygon.info
      });
    });
  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to addEventListenerClickPolygon", err);
  }
}

function addEventListenerHoverPolygon(polygon) {
  try {
    if (!polygon?.info?.polygonColor) return false;

    const colors = polygon.info.polygonColor;

    window.google.maps.event.addListener(polygon, "mouseover", function () {
      const opts = {
        strokeOpacity: polygonsOpts.hover.STROKE_OPACITY,
        strokeWeight: polygonsOpts.hover.STROKE_WEIGHT,
        fillOpacity: polygonsOpts.hover.FILL_OPACITY,
        strokeColor: colors.hover.stroke ? colors.hover.stroke : polygonsOpts.hover.STROKE_COLOR,
        fillColor: colors.hover.fill ? colors.hover.fill : polygonsOpts.hover.FILL
      };
      this.setOptions(opts);
    });
    window.google.maps.event.addListener(polygon, "mouseout", function () {
      const opts = {
        strokeOpacity: polygonsOpts.STROKE_OPACITY,
        strokeWeight: polygonsOpts.STROKE_WEIGHT,
        fillOpacity: polygonsOpts.FILL_OPACITY,
        strokeColor: colors.stroke ? colors.stroke : polygonsOpts.STROKE_COLOR,
        fillColor: colors.fill ? colors.fill : polygonsOpts.FILL
      };
      this.setOptions(opts);
    });
  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to addEventListenerHoverPolygon", err);
  }
}

function addEventListenerClickPin(marker, infoWindow) {
  try {
    if (!marker) return false;

    marker.addListener("click", () => {
      if (infoWindow) infoWindow.open(marker.get("map"), marker);
    });
  } catch (err) {
    logUtils.logErrorMsg(TAG, "Unable to addEventListenerClickPin", err);
  }
}
